import React, { useState, useEffect, useCallback } from "react";
import {
	Modal,
	Input,
	Button,
	Card,
	Checkbox,
	Typography,
	Divider,
	Empty,
	Space,
} from "antd";
import { SearchOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { globalProductSearch } from "../../redux/actions/product/globalSearchAction";
import { debounce } from "../../utils/debounce";

const { Text } = Typography;

const ProductSearchModal = ({
	visible,
	onClose,
	allProducts,
	onProductSelect,
	onProductsSelectBulk,
	onAddNewProduct,
	selectedProds = [],
}) => {
	const [searchValue, setSearchValue] = useState("");
	const [filteredProducts, setFilteredProducts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedItems, setSelectedItems] = useState([]);
	const dispatch = useDispatch();

	useEffect(() => {
		if (visible && allProducts) {
			setFilteredProducts(allProducts);
			setSearchValue("");
			setSelectedItems([]);
		}
	}, [visible, allProducts]);

	const debouncedSearch = useCallback(
		debounce(async (searchTerm) => {
			if (!searchTerm || searchTerm.trim() === "") {
				setFilteredProducts(allProducts || []);
				setLoading(false);
				return;
			}

			setLoading(true);
			try {
				const result = await dispatch(
					globalProductSearch(searchTerm.trim(), 1, 100)
				);
				if (result && result.status === "success") {
					setFilteredProducts(result.data || []);
				}
			} catch (error) {
				console.error("Search error:", error);
				const term = searchTerm.toLowerCase();
				const filtered = (allProducts || []).filter(
					(product) =>
						product.name?.toLowerCase().includes(term) ||
						product.isbn?.toLowerCase().includes(term) ||
						product.author?.toLowerCase().includes(term) ||
						product.book_publisher?.name?.toLowerCase().includes(term)
				);
				setFilteredProducts(filtered);
			} finally {
				setLoading(false);
			}
		}, 500),
		[allProducts, dispatch]
	);

	const handleSearch = (value) => {
		setSearchValue(value);
		debouncedSearch(value);
	};

	const handleProductToggle = (product, checked) => {
		setSelectedItems((prev) => {
			const exists = prev.some((p) => p.id === product.id);
			if (checked && !exists) return [...prev, product];
			if (!checked && exists) return prev.filter((p) => p.id !== product.id);
			return prev;
		});
	};

	const selectableProducts = filteredProducts.filter(
		(p) => !selectedProds.some((sp) => sp.id === p.id)
	);

	const allVisibleSelected =
		selectableProducts.length > 0 &&
		selectableProducts.every((p) => selectedItems.some((s) => s.id === p.id));

	const handleSelectAllVisible = (checked) => {
		if (checked) {
			setSelectedItems((prev) => {
				const byId = new Map(prev.map((p) => [p.id, p]));
				selectableProducts.forEach((p) => byId.set(p.id, p));
				return Array.from(byId.values());
			});
		} else {
			const visibleIds = new Set(selectableProducts.map((p) => p.id));
			setSelectedItems((prev) => prev.filter((p) => !visibleIds.has(p.id)));
		}
	};

	const handleAddSelectedProducts = () => {
		if (selectedItems.length === 0) return;

		if (typeof onProductsSelectBulk === "function") {
			onProductsSelectBulk(selectedItems);
		} else {
			const baseTimestamp = Date.now();
			selectedItems.forEach((product, index) => {
				const uniqueKey =
					baseTimestamp + index + product.id * 1000000 + Math.random() * 1000;
				onProductSelect(product, uniqueKey);
			});
		}
		onClose();
	};

	const handleAddNewProduct = () => {
		onAddNewProduct();
		onClose();
	};

	return (
		<Modal
			title={`Select Books ${
				selectedItems.length > 0 ? `(${selectedItems.length} selected)` : ""
			}`}
			open={visible}
			onCancel={onClose}
			footer={
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						gap: 10,
						flexWrap: "wrap",
					}}
				>
					<Button onClick={onClose}>Cancel</Button>
					<Space>
						{selectedItems.length > 0 && (
							<Button onClick={() => setSelectedItems([])}>Clear</Button>
						)}
						<Button
							type="primary"
							onClick={handleAddSelectedProducts}
							disabled={selectedItems.length === 0}
						>
							Add{" "}
							{selectedItems.length > 0
								? `${selectedItems.length} Book${
										selectedItems.length > 1 ? "s" : ""
								  }`
								: "Selected"}{" "}
							to Invoice
						</Button>
					</Space>
				</div>
			}
			width="90%"
			style={{ maxWidth: 700 }}
			bodyStyle={{ padding: 16 }}
		>
			<div className="mb-3">
				<Input
					size="large"
					placeholder="Search by ISBN, Title, Author, or Publisher"
					prefix={<SearchOutlined />}
					value={searchValue}
					onChange={(e) => handleSearch(e.target.value)}
					style={{ marginBottom: 12 }}
					allowClear
				/>
			</div>

			<div className="mb-3" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
				<Button
					type="dashed"
					icon={<PlusOutlined />}
					onClick={handleAddNewProduct}
					style={{ flex: 1, minWidth: 160, height: 40 }}
				>
					Add New Product
				</Button>
			</div>

			{filteredProducts.length > 0 && (
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: 8,
					}}
				>
					<Checkbox
						checked={allVisibleSelected}
						indeterminate={
							selectedItems.length > 0 &&
							!allVisibleSelected &&
							selectableProducts.some((p) =>
								selectedItems.some((s) => s.id === p.id)
							)
						}
						onChange={(e) => handleSelectAllVisible(e.target.checked)}
						disabled={selectableProducts.length === 0}
					>
						Select all visible ({selectableProducts.length})
					</Checkbox>
					<Text type="secondary" style={{ fontSize: 12 }}>
						{selectedItems.length} selected
					</Text>
				</div>
			)}

			<Divider style={{ margin: "8px 0 12px" }} />

			<div style={{ maxHeight: 420, overflowY: "auto" }}>
				{loading ? (
					<div style={{ textAlign: "center", padding: 20 }}>
						<Text>Searching...</Text>
					</div>
				) : filteredProducts.length > 0 ? (
					filteredProducts.map((product) => {
						const isSelected = selectedItems.some((p) => p.id === product.id);
						const isAlreadyInInvoice = selectedProds.some(
							(p) => p.id === product.id
						);

						return (
							<Card
								key={product.id}
								size="small"
								hoverable={!isAlreadyInInvoice}
								onClick={() => {
									if (!isAlreadyInInvoice) {
										handleProductToggle(product, !isSelected);
									}
								}}
								style={{
									marginBottom: 8,
									cursor: isAlreadyInInvoice ? "not-allowed" : "pointer",
									border: isSelected
										? "2px solid #1890ff"
										: "1px solid #d9d9d9",
									backgroundColor: isSelected
										? "#e6f7ff"
										: isAlreadyInInvoice
										? "#fafafa"
										: "white",
									opacity: isAlreadyInInvoice ? 0.7 : 1,
								}}
								bodyStyle={{ padding: "10px 12px" }}
							>
								<div
									style={{
										display: "flex",
										alignItems: "flex-start",
										gap: 12,
									}}
								>
									<div
										onClick={(e) => e.stopPropagation()}
										style={{ paddingTop: 2 }}
									>
										<Checkbox
											checked={isSelected}
											disabled={isAlreadyInInvoice}
											onChange={(e) =>
												handleProductToggle(product, e.target.checked)
											}
										/>
									</div>

									{product.imageUrl ? (
										<img
											src={product.imageUrl}
											alt={product.name}
											style={{
												width: 40,
												height: 40,
												objectFit: "cover",
												borderRadius: 4,
												flexShrink: 0,
											}}
										/>
									) : (
										<div
											style={{
												width: 40,
												height: 40,
												backgroundColor: "#f0f0f0",
												borderRadius: 4,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												fontSize: 10,
												color: "#999",
												flexShrink: 0,
											}}
										>
											No Img
										</div>
									)}

									<div style={{ flex: 1, minWidth: 0 }}>
										<Text strong style={{ fontSize: 14, display: "block" }}>
											{product.name}
											{isAlreadyInInvoice && (
												<span
													style={{
														marginLeft: 8,
														fontSize: 11,
														color: "#52c41a",
														fontWeight: "normal",
													}}
												>
													Already in invoice
												</span>
											)}
										</Text>
										<Text
											type="secondary"
											style={{ fontSize: 12, display: "block" }}
										>
											ISBN: {product.isbn || "-"}
										</Text>
										{product.author && (
											<Text
												type="secondary"
												style={{ fontSize: 12, display: "block" }}
											>
												Author: {product.author}
											</Text>
										)}
										{product.book_publisher?.name && (
											<Text
												type="secondary"
												style={{ fontSize: 12, display: "block" }}
											>
												Publisher: {product.book_publisher.name}
											</Text>
										)}
									</div>

									<div style={{ textAlign: "right", minWidth: 72 }}>
										<Text strong style={{ fontSize: 14, display: "block" }}>
											₹{product.sale_price || 0}
										</Text>
										<Text
											type="secondary"
											style={{ fontSize: 12, display: "block" }}
										>
											Stock: {product.quantity || 0}
										</Text>
									</div>
								</div>
							</Card>
						);
					})
				) : (
					<Empty
						description={
							searchValue
								? `No products found for "${searchValue}"`
								: "No products available"
						}
						style={{ padding: "40px 0" }}
					>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={handleAddNewProduct}
						>
							Add New Product
						</Button>
					</Empty>
				)}
			</div>

			{filteredProducts.length > 0 && (
				<div style={{ marginTop: 12, textAlign: "center" }}>
					<Text type="secondary" style={{ fontSize: 12 }}>
						{filteredProducts.length} book
						{filteredProducts.length !== 1 ? "s" : ""} found — tick checkboxes,
						then add to invoice
					</Text>
				</div>
			)}
		</Modal>
	);
};

export default ProductSearchModal;
